import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { StoreShipmentInterface } from '../../interfaces'
import { PrimaryButton, SecondaryButton } from '../Atoms/Buttons'
import { ModalWidthFull } from '../Atoms/ModalWidthFull'
import { DowloadShipmentsMultiples } from '../../services/ShipmentServices'

interface shipmentMultiPrintModalProps {
  open: boolean
  /**
   * Solo se leen estos tres campos, así que el tipo se pide al mínimo: hay llamadores
   * (el detalle de carga masiva) que no tienen `StoreShipmentInterface` completos, y
   * fabricar objetos falsos casteados para satisfacer 40 campos que nadie usa sería
   * mentirle al tipo. Los llamadores que sí pasan `StoreShipmentInterface[]` siguen
   * compilando. Mismo criterio que `ShipmentLabelDjangoModal`.
   */
  shipment: Pick<
    StoreShipmentInterface,
    'shipmentNumber' | 'serviceCode' | 'buCodeSource'
  >[]
  onPrint: () => void
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  url: string
  type: number
  /**
   * Navegación por tandas, para selecciones grandes. Opcional: sin esto el modal se
   * comporta como antes. Mismo contrato que el `batch` de `ShipmentLabelDjangoModal`.
   */
  batch?: {
    index: number
    total: number
    onChange: (index: number) => void
  }
}

interface ListShipmentDeclaredDocumentRequestInterfase {
  shipmentNumber: string
  serviceID: string
  bUCodeSource: string
}

export const ShipmentMultiPrintModal: FC<shipmentMultiPrintModalProps> = ({
  open,
  shipment,
  onPrint,
  setOpen,
  url,
  type,
  batch,
}) => {
  /**
   * Ref propio, NO `document.querySelector('iframe')`.
   *
   * La versión anterior escribía en el primer iframe de TODO el documento: si convivía
   * con otro modal que también monta un iframe (el detalle de carga masiva monta
   * `DispatchPrintModal` sin condición), el PDF terminaba en el visor equivocado.
   */
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const requestList = useMemo<ListShipmentDeclaredDocumentRequestInterfase[]>(
    () =>
      shipment.map((item) => ({
        shipmentNumber: item.shipmentNumber.toString(),
        serviceID: item?.serviceCode?.toString() ?? '60',
        bUCodeSource: item?.buCodeSource?.toString() ?? '',
      })),
    [shipment]
  )

  /**
   * La descarga vive en un efecto, no en el cuerpo del render.
   *
   * Antes se llamaba directo al renderizar, así que disparaba el POST en CADA render.
   * El detalle de carga masiva re-renderiza cada ~2 s por el polling de la carga: eso
   * era una descarga cada dos segundos mientras el modal estuviera abierto.
   */
  useEffect(() => {
    if (!open || requestList.length === 0) return

    let objectUrl: string | null = null
    let active = true

    setLoading(true)
    setError(null)

    const run = async () => {
      const response = await DowloadShipmentsMultiples(requestList, url, type)

      // Sin este chequeo un 500 se convierte en un blob perfectamente válido que se
      // pinta en el visor: el operador ve basura o una hoja en blanco y no sabe si el
      // problema es del sistema, de la guía o de su impresora.
      if (!response?.ok) {
        throw new Error(
          `El servidor no pudo generar el documento (HTTP ${
            response?.status ?? '?'
          }).`
        )
      }

      const contentType = response.headers?.get?.('content-type') ?? ''
      if (contentType.includes('application/json')) {
        // El backend responde 200 con un envelope de error en algunos casos.
        const detail = await response.text()
        throw new Error(
          detail?.slice(0, 300) || 'El servidor devolvió un error sin detalle.'
        )
      }

      const blob = await response.blob()
      if (blob.size === 0) {
        throw new Error(
          'El servidor devolvió un documento vacío para las guías seleccionadas.'
        )
      }

      if (!active) return
      objectUrl = window.URL.createObjectURL(blob)
      if (iframeRef.current) iframeRef.current.src = objectUrl
      setLoading(false)
    }

    run().catch((e: any) => {
      if (!active) return
      setLoading(false)
      setError(e?.message || 'No se pudo generar el documento.')
    })

    return () => {
      active = false
      if (objectUrl) window.URL.revokeObjectURL(objectUrl)
    }
  }, [open, requestList, url, type])

  return (
    <ModalWidthFull
      open={open}
      setOpen={setOpen}
      className="flex flex-1 mx-4 my-8"
      style={{ maxWidth: '90rem', height: '50rem' }}
    >
      <div className="flex flex-1 flex-col">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">
            <span className="text-gray-700 hidden sm:inline">
              Impresión de guías:{' '}
            </span>
          </h2>

          <div className="ml-3 flex h-7 items-center">
            <button
              type="button"
              className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-main-500"
              onClick={() => setOpen(false)}
            >
              <span className="absolute -inset-2.5" />
              <span className="sr-only">Close panel</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>

        <hr className="border-gray-300 my-3" />

        {/* Shipment Data */}
        <div className="flex flex-1 items-center justify-center overflow-hidden">
          {loading && (
            <p className="text-lg text-gray-600">Generando documento...</p>
          )}
          {!loading && error && (
            <p className="max-w-xl text-center text-lg text-red-600">{error}</p>
          )}
          <iframe
            ref={iframeRef}
            title="Documento"
            src=""
            width="100%"
            height="100%"
            className={loading || error ? 'hidden' : ''}
          />
        </div>

        <hr className="border-gray-300 my-6" />

        {/* Footer */}
        <div className="flex justify-between items-center">
          <PrimaryButton onClick={() => setOpen(false)}>
            <span className="w-20">Cerrar</span>
          </PrimaryButton>

          {batch && batch.total > 1 && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <SecondaryButton
                disabled={batch.index === 0 || loading}
                onClick={() => batch.onChange(batch.index - 1)}
                className="!px-3"
              >
                Anterior
              </SecondaryButton>
              <span className="whitespace-nowrap tabular-nums">
                Grupo {batch.index + 1} de {batch.total}
              </span>
              <SecondaryButton
                disabled={batch.index >= batch.total - 1 || loading}
                onClick={() => batch.onChange(batch.index + 1)}
                className="!px-3"
              >
                Siguiente
              </SecondaryButton>
            </div>
          )}
        </div>
      </div>
    </ModalWidthFull>
  )
}
